import { z } from 'zod';
import { ARIA_ROLES, EXTRACT_MODES, SELECTOR_PACK_LIMITS } from './constants';

/**
 * A single locator candidate.
 *
 * Every variant is a *description of how to find an element*. None of them can
 * carry code: `css` and `xpath` are passed to Playwright's locator engine, which
 * queries the DOM — it never evaluates page script. There is deliberately no
 * `evaluate`, `script`, or `function` strategy, and adding one would break the
 * security boundary documented in `constants.ts`.
 */

const selectorString = z.string().min(1).max(SELECTOR_PACK_LIMITS.maxSelectorLength);
const textString = z.string().min(1).max(SELECTOR_PACK_LIMITS.maxTextLength);

/** Narrow a candidate's matches before picking one. */
export const locatorFilterSchema = z
  .object({
    hasText: textString.optional(),
    hasNotText: textString.optional(),
    /** Zero-based index into the matched set; negative counts from the end. */
    nth: z.number().int().min(-50).max(200).optional(),
    /** Require the element to be visible. Defaults to true for click targets. */
    visibleOnly: z.boolean().optional(),
  })
  .strict();

export type LocatorFilter = z.infer<typeof locatorFilterSchema>;

const baseCandidate = {
  filter: locatorFilterSchema.optional(),
  /** Free-text note for the admin editing the pack; never used at runtime. */
  note: z.string().max(200).optional(),
  /**
   * Per-candidate override of the field rule's `extract` mode (adapter v2).
   *
   * One field, several card generations: the same `postedAt` is an accessible
   * name on legacy cards and a geometry-scrambled span run on 2026-08 cards.
   * A single rule-level mode cannot serve both, and splitting the field would
   * fork every consumer. Rule candidates (clicking targets) simply never read
   * these — surplus data on them is inert, which is why the fields live on the
   * shared candidate rather than a parallel schema.
   */
  extract: z.enum(EXTRACT_MODES).optional(),
  /** Attribute name when this candidate's `extract` is `attribute`. */
  attribute: z.string().min(1).max(64).optional(),
};

export const locatorCandidateSchema = z.discriminatedUnion('strategy', [
  z
    .object({
      strategy: z.literal('role'),
      role: z.enum(ARIA_ROLES),
      name: textString.optional(),
      exact: z.boolean().optional(),
      ...baseCandidate,
    })
    .strict(),
  z
    .object({
      strategy: z.literal('testId'),
      value: selectorString,
      ...baseCandidate,
    })
    .strict(),
  z
    .object({
      strategy: z.literal('label'),
      value: textString,
      exact: z.boolean().optional(),
      ...baseCandidate,
    })
    .strict(),
  z
    .object({
      strategy: z.literal('placeholder'),
      value: textString,
      exact: z.boolean().optional(),
      ...baseCandidate,
    })
    .strict(),
  z
    .object({
      strategy: z.literal('altText'),
      value: textString,
      exact: z.boolean().optional(),
      ...baseCandidate,
    })
    .strict(),
  z
    .object({
      strategy: z.literal('title'),
      value: textString,
      exact: z.boolean().optional(),
      ...baseCandidate,
    })
    .strict(),
  z
    .object({
      strategy: z.literal('text'),
      value: textString,
      exact: z.boolean().optional(),
      ...baseCandidate,
    })
    .strict(),
  z
    .object({
      strategy: z.literal('css'),
      value: selectorString,
      ...baseCandidate,
    })
    .strict(),
  z
    .object({
      strategy: z.literal('xpath'),
      value: selectorString,
      ...baseCandidate,
    })
    .strict(),
]);

export type LocatorCandidate = z.infer<typeof locatorCandidateSchema>;

/**
 * An ordered list of candidates for one named element.
 *
 * Order is the fallback chain: the interpreter tries candidates top to bottom and
 * uses the first that resolves. Put stable locators first — `role`, `aria-label`,
 * visible text — and leave randomly generated CSS classes for last, because
 * Facebook rotates those constantly.
 */
export const locatorRuleSchema = z
  .object({
    candidates: z
      .array(locatorCandidateSchema)
      .min(1)
      .max(SELECTOR_PACK_LIMITS.maxCandidatesPerRule),
    /** Fail the step when no candidate matches (vs. treating it as absent). */
    required: z.boolean().default(false),
    /** Per-rule wait budget. Capped so one broken rule cannot stall a cycle. */
    timeoutMs: z.number().int().min(100).max(30_000).default(5_000),
    description: z.string().max(300).optional(),
  })
  .strict();

export type LocatorRule = z.infer<typeof locatorRuleSchema>;
